"use client";

import React, { useRef, useState, MouseEvent } from "react";
import { ShieldAlert, Zap, Wrench, CircleCheck, Users, TrendingUp } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const features = [{
  title: "Licensed & Insured",
  description: "Our operations are fully backed by comprehensive liability insurance and Australian industry certifications, ensuring total security and peace of mind for every site.",
  icon: ShieldAlert
}, {
  title: "Peak Efficiency",
  description: "We leverage advanced logistical planning and high-performance equipment to minimise downtime and exceed your project delivery timelines.",
  icon: Zap
}, {
  title: "Modern Fleet",
  description: "Access a diverse range of late-model machinery maintained to rigorous standards, ready to tackle the most demanding industrial and civil challenges across Australia.",
  icon: TrendingUp
}, {
  title: "Commitment to Quality & Safety",
  description: "We adhere to strict Australian standards and WHS protocols, ensuring every project meets the highest benchmarks for safety and regulatory excellence.",
  icon: CircleCheck
}, {
  title: "Skilled Subcontractor Network",
  description: "Our vetted network of specialist tradespeople ensures that every aspect of your build is handled by experts dedicated to superior craftsmanship and local compliance.",
  icon: Users
}, {
  title: "Reliable Project Delivery",
  description: "With a focus on proactive communication and agile management, we ensure your project stays on track and within budget, every step of the way.",
  icon: TrendingUp
}];
function TiltCard({
  feature,
  index
}: {
  feature: (typeof features)[0];
  index: number;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = (y - centerY) / centerY * -15;
    const rotateY = (x - centerX) / centerX * 15;
    cardRef.current.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
  };
  const handleMouseLeave = () => {
    setIsHovered(false);
    if (!cardRef.current) return;
    cardRef.current.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)`;
  };
  const handleMouseEnter = () => {
    setIsHovered(true);
  };
  const Icon = feature.icon;
  return <div data-section-id="222770" data-index={index} className="relative group h-full w-full" style={{
    perspective: "1000px"
  }}>
      {}
      <div ref={cardRef} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} onMouseEnter={handleMouseEnter} className="h-full w-full rounded-sm bg-gradient-to-br from-primary/40 via-primary/10 to-transparent p-[1px] transition-all duration-300 ease-out will-change-transform">
        <Card className="relative h-full w-full overflow-hidden border-none bg-secondary/80 backdrop-blur-xl transition-colors duration-500 group-hover:bg-secondary/90 rounded-sm">
          {}
          <div className={`absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 transition-opacity duration-500 ${isHovered ? "opacity-100" : ""}`} />
          {}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(59,130,246,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(59,130,246,0.03)_1px,transparent_1px)] bg-[size:20px_20px]" />

          <CardHeader className="relative z-10 pb-4 pt-10">
            <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-sm bg-primary/10 shadow-sm ring-1 ring-primary/30 transition-transform duration-500 group-hover:scale-110 group-hover:shadow-primary/20">
              <Icon className="h-8 w-8 text-primary" strokeWidth={1.5} />
            </div>
            <CardTitle className="font-default text-2xl font-extrabold tracking-tight text-secondary-foreground uppercase">
              {feature.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="relative z-10 pb-10">
            <CardDescription className="font-default text-base leading-relaxed text-secondary-foreground/80">
              {feature.description}
            </CardDescription>
          </CardContent>
        </Card>
      </div>
    </div>;
}
export default function WhyThisIntensive() {
  return <section id="key-strengths" className="relative overflow-hidden bg-secondary py-32 text-secondary-foreground">
      {}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute left-1/4 top-1/4 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/10 mix-blend-screen blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 h-[600px] w-[600px] translate-x-1/3 translate-y-1/3 rounded-full bg-primary/5 mix-blend-screen blur-[150px]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(59,130,246,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(59,130,246,0.05)_1px,transparent_1px)] bg-[size:40px_40px] opacity-40" />
      </div>

      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-20 text-center">
          <h2 className="font-default text-4xl font-extrabold tracking-tight sm:text-5xl md:text-6xl text-secondary-foreground">
            Our Key{" "}
            <span className="text-primary">Capabilities</span>
          </h2>
          <p className="mx-auto mt-6 max-w-2xl font-default text-lg text-secondary-foreground/80">
            Built on a foundation of reliability and precision, we provide the industrial 
            infrastructure and expertise required for large-scale success across the Australian market.
          </p>
        </div>

        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 md:grid-cols-3">
          {features.map((feature, index) => <TiltCard key={index} feature={feature} index={index} />)}
        </div>
      </div>
    </section>;
}